import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import AppBar from "@mui/material/AppBar";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import { BrandLockup } from "../../components/Brand";
import { ColorModeToggle } from "../../components/ColorModeToggle";
import { NamespaceGuide } from "./NamespaceGuide";
import { submitApplication, ApplicationInvalid } from "../../api/intake";
import { ApiError } from "../../api/errors";
import Link from "@mui/material/Link";
import {
  validateApplication,
  clearGstinIfUnregistered,
  normalizeNamespace,
  isValidIfsc,
  EMPTY_APPLICATION,
  OPERATOR_TYPES,
  REGISTRATION_STATUSES,
  FILING_FREQUENCIES,
  LICENSE_TYPES,
  type OperatorApplication,
  type TaxType,
} from "../../domain/operator";
import { PROVIDER, SELECTABLE_PROVIDER_KINDS, anyKindRequiresLicence } from "../../domain/labels";
import { TierDisclosure } from "./TierDisclosure";
import { VersionLine } from "../../components/VersionLine";
import { HumanCheck } from "../../components/HumanCheck";

/**
 * The operator application.
 *
 * ⛔ PUBLIC AND UNAUTHENTICATED. It reads no identity and calls no gateway
 * route. That is not an oversight — it is the whole design: an applicant has
 * no token, no namespace and no role, so there is nothing for a gateway call
 * to be scoped by.
 *
 * 🔑 SIGNUP CREATES AN ACCOUNT, BUT NOT STANDING. Owner decision 2026-08-31,
 * superseding this form's original "application only" design: the applicant
 * gets a ZITADEL identity and a namespace immediately and logs straight in,
 * because a twenty-five field cold form is abandoned and licence verification
 * takes days either way. What they do NOT get is approval —
 * radaccess_operator_profile.active lands false, and radbilling refuses to name
 * an unapproved supplier on an invoice.
 *
 * ⛔ THE NAMESPACE IS ASSIGNED, NEVER ACCEPTED. That claim is what EVERY
 * containment check on this platform trusts, so letting a signup form choose it
 * would let an anonymous visitor plant themselves inside another provider's
 * subtree before anyone approved anything.
 *
 * 🛑 NOT WIRED YET. Nothing in the estate creates ZITADEL users, and the
 * credential to do it must be provisioned by the platform owner — the two
 * service-account keys that exist were issued for other purposes and must not
 * be repurposed to mutate identity. Until then this form validates and submits
 * nowhere.
 */
export function OperatorRegister() {
  const [app, setApp] = useState<OperatorApplication>(EMPTY_APPLICATION);
  // 🔑 FOUR STATES, NOT A BOOLEAN. "submitted" alone cannot distinguish sending
  // from sent from refused, and a form that looks sent while it is still in
  // flight invites a second submission -- which on a public write path means a
  // duplicate application a reviewer has to reconcile by hand.
  const [sending, setSending] = useState(false);
  const [acceptedId, setAcceptedId] = useState<string | null>(null);
  const [problems, setProblems] = useState<string[] | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  /**
   * Submit, once.
   *
   * 🔑 EVERY PRIOR OUTCOME IS CLEARED FIRST. Leaving the last error on screen
   * while a new attempt is in flight shows the applicant a refusal that no
   * longer applies, and if the retry succeeds they see success AND failure at
   * once.
   *
   * 🚨 The button is also disabled once accepted. This is a public,
   * unauthenticated write path with no idempotency key: a second click is a
   * second application, and a reviewer then has two rows for one operator with
   * no way to tell which is current.
   */
  const send = async () => {
    setSending(true);
    setProblems(null);
    setFailure(null);
    try {
      const { id } = await submitApplication(app);
      setAcceptedId(id);
    } catch (e) {
      // 🔑 A REFUSAL AND AN OUTAGE ARE DIFFERENT EVENTS. ApplicationInvalid
      // carries the server's reasons and the applicant can act on them;
      // everything else means the request may never have arrived.
      if (e instanceof ApplicationInvalid) setProblems(e.problems);
      else if (e instanceof ApiError) setFailure(e.message);
      else setFailure(String(e));
    } finally {
      setSending(false);
    }
  };

  const invalid = validateApplication(app);
  // Only internet and VPN providers are licensed; everything else is not asked.
  const licensed = anyKindRequiresLicence(app.providerKinds);
  // A dialog rather than a link away: the form holds unsaved input and nothing
  // persists it, so navigating off to read the guide would lose the lot.
  const [guideOpen, setGuideOpen] = useState(false);
  const set = <K extends keyof OperatorApplication>(k: K, v: OperatorApplication[K]) =>
    setApp((a) => ({ ...a, [k]: v }));
  const setTax = (t: TaxType, field: "taxnum" | "taxdate", v: string) =>
    setApp((a) => ({
      ...a,
      tax: a.tax.map((e) => (e.taxtype === t ? { ...e, [field]: v } : e)),
    }));
  const tax = (t: TaxType, field: "taxnum" | "taxdate") =>
    app.tax.find((e) => e.taxtype === t)?.[field] ?? "";

  /**
   * An UNREGISTERED operator has no GSTIN, so the field is disabled — and
   * CLEARED in the same action.
   *
   * 🪤 GREYING OUT ALONE IS THE BUG, NOT THE FIX. A disabled input keeps its
   * value and still submits it. Someone who types a GSTIN and then switches to
   * UNREGISTERED would send a number they can no longer see, and radaccess's
   * validateTaxInfo refuses exactly that pair — so the form would report an
   * error about a field it is hiding. Clearing on the transition is what makes
   * the disabled state honest.
   */
  const setRegistrationStatus = (v: OperatorApplication["registrationStatus"]) =>
    setApp((a) => ({
      ...a,
      registrationStatus: v,
      tax: clearGstinIfUnregistered(a.tax, v),
    }));

  const unregistered = app.registrationStatus === "UNREGISTERED";

  const field = (
    k: keyof OperatorApplication,
    label: string,
    helper?: string,
    span: number = 4,
  ) => (
    <Grid size={{ xs: 12, sm: 6, md: span }}>
      <TextField
        fullWidth
        size="small"
        label={label}
        value={String(app[k] ?? "")}
        onChange={(e) => set(k, e.target.value as OperatorApplication[typeof k])}
        {...(helper ? { helperText: helper } : {})}
      />
    </Grid>
  );

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <AppBar position="static" color="default" elevation={0}>
        <Toolbar>
          <BrandLockup to="/welcome" />
          <ColorModeToggle />
          <Button size="small" component={RouterLink} to="/welcome">
            Back
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Register as a {PROVIDER}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          You get a login and your own namespace straight away, so you can set up plans and
          subscribers immediately. Your account stays <strong>unapproved</strong> — and cannot issue
          an invoice — until we have verified your PAN, GSTIN and licence.
        </Typography>

        <Alert severity="info" sx={{ mb: 3 }}>
          <AlertTitle>What we ask, and why</AlertTitle>
          <strong>PAN is required</strong>; GSTIN is not, because composition and unregistered
          providers are normal. Your <strong>state</strong> determines place of supply on every
          invoice. <strong>Bank details are required</strong> — settlement from the payment gateway
          has to land somewhere.{" "}
          {licensed ? (
            <>
              You sell a <strong>licensed service</strong> (internet or VPN), so we also need
              either your <strong>own licence</strong> or your <strong>upstream provider</strong> —
              internet service is invoiced in the licence holder&apos;s name. Your other lines,
              such as hosting or OTT, need no licence and are unaffected.
            </>
          ) : (
            <>
              <strong>No licence is asked for.</strong> Only internet and VPN service is
              licensed; hosting, cloud, OTT, SaaS and infrastructure are not, so we do not ask
              you for a document that does not exist for your business. Tick internet or VPN
              above if you also sell those.
            </>
          )}
        </Alert>

        {/* 🔑 THE PRICE COMES BEFORE THE FORM, not after it. Twenty-five
            fields asked before anyone has said what the thing costs is how a
            cold form gets abandoned -- and a price disclosed only after the
            work is done reads as a bait. It is a disclosure and not a step:
            there is nothing to fill in, which is why it sits outside the
            Paper below rather than becoming its first section. */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <TierDisclosure dense />
        </Paper>

        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Divider textAlign="left"><Typography variant="caption">The business</Typography></Divider>
            </Grid>
            {field("legalName", "Legal name", "Exactly as registered", 6)}
            {/* 🔑 Placed here because it GOVERNS the licence section below. A
                picker that changes which fields are required has to be answered
                before the fields it changes. */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                select fullWidth size="small" label="Services you provide"
                slotProps={{ select: { multiple: true, renderValue: (v: unknown) =>
                  (v as string[]).map((k) => SELECTABLE_PROVIDER_KINDS.find((s) => s.key === k)?.name ?? k).join(", ") } }}
                helperText={
                  licensed
                    ? "Includes a licensed service — we ask for a licence or upstream"
                    : "Tick every service you sell"
                }
                value={app.providerKinds}
                onChange={(e) =>
                  set("providerKinds", typeof e.target.value === "string"
                    ? e.target.value.split(",")
                    : (e.target.value as unknown as string[]))
                }
              >
                {SELECTABLE_PROVIDER_KINDS.map((k) => (
                  <MenuItem key={k.key} value={k.key}>
                    <Checkbox checked={app.providerKinds.includes(k.key)} size="small" sx={{ p: 0.5, mr: 1 }} />
                    {k.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                select fullWidth size="small" label="Licence classification"
                helperText="Not your AnkhOS plan -- this is what you are licensed as"
                value={app.operatorType}
                onChange={(e) => set("operatorType", e.target.value as OperatorApplication["operatorType"])}
              >
                {OPERATOR_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
            </Grid>
            {/* 🔑 THE GATE. The namespace is permanent and is the identity every
                containment check trusts, so the field stays disabled until the
                applicant confirms they have read what they are choosing. This
                exists to remove the manual namespace review that blocks
                automatic onboarding — not as a liability disclaimer. */}
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={app.namespaceGuideRead}
                    onChange={(e) => set("namespaceGuideRead", e.target.checked)}
                  />
                }
                label={
                  <Typography variant="body2">
                    I have read{" "}
                    <Link component="button" type="button" onClick={(e) => { e.preventDefault(); setGuideOpen(true); }}>
                      how namespaces work
                    </Link>{" "}
                    and understand my namespace is permanent.
                  </Typography>
                }
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                disabled={!app.namespaceGuideRead}
                fullWidth size="small" label="Requested namespace"
                helperText={
                  !app.namespaceGuideRead
                    ? "Read the guide above first"
                    : app.requestedNamespace && normalizeNamespace(app.requestedNamespace) !== app.requestedNamespace
                      ? `Stored as ${normalizeNamespace(app.requestedNamespace)}`
                      : "e.g. net.example.pune"
                }
                value={app.requestedNamespace}
                onChange={(e) => set("requestedNamespace", e.target.value)}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Divider textAlign="left"><Typography variant="caption">Tax</Typography></Divider>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField fullWidth size="small" label="PAN (required)"
                value={tax("PAN", "taxnum")}
                onChange={(e) => setTax("PAN", "taxnum", e.target.value.toUpperCase())} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField fullWidth size="small" type="date" label="PAN date"
                slotProps={{ inputLabel: { shrink: true } }}
                value={tax("PAN", "taxdate")}
                onChange={(e) => setTax("PAN", "taxdate", e.target.value)} />
            </Grid>
            {/* Registration status governs the three fields after it, so it
                comes first: a control that greys out inputs ABOVE itself reads
                as a glitch. */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField select fullWidth size="small" label="Registration status"
                helperText={unregistered ? "No GSTIN, and no GST on your invoices" : " "}
                value={app.registrationStatus}
                onChange={(e) => setRegistrationStatus(e.target.value as OperatorApplication["registrationStatus"])}>
                {REGISTRATION_STATUSES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField fullWidth size="small" label="GSTIN"
                disabled={unregistered}
                helperText={unregistered ? "Not applicable when unregistered" : "Must embed the PAN above"}
                value={tax("GSTIN", "taxnum")}
                onChange={(e) => setTax("GSTIN", "taxnum", e.target.value.toUpperCase())} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField fullWidth size="small" type="date" label="GST registration date"
                disabled={unregistered}
                slotProps={{ inputLabel: { shrink: true } }}
                value={tax("GSTIN", "taxdate")}
                onChange={(e) => setTax("GSTIN", "taxdate", e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField select fullWidth size="small" label="GST filing frequency"
                disabled={unregistered}
                helperText={unregistered ? "Nothing to file" : " "}
                value={app.filingFrequency}
                onChange={(e) => set("filingFrequency", e.target.value as OperatorApplication["filingFrequency"])}>
                {FILING_FREQUENCIES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
            </Grid>

            {/* 🔑 Settlement account, asked of EVERY kind. The money path does not
                care whether the subscriber bought broadband or a SaaS seat.

                ⚠️ Collecting is not verifying. radaccess_operator_bankinfo
                .verified defaults to 0 and the owner ruled bank verification is
                mandatory before approval, so a reviewer still has to confirm
                these -- the format check below only rejects what CANNOT be
                right. */}
            <Grid size={{ xs: 12 }}>
              <Divider textAlign="left">
                <Typography variant="caption">Bank account for settlement</Typography>
              </Divider>
            </Grid>
            {field("bankAccountHolder", "Account holder", "As it appears on the account", 3)}
            {field("bankAccountNumber", "Account number", "Digits only", 3)}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth size="small" label="IFSC"
                helperText={
                  app.bankIfsc && !isValidIfsc(app.bankIfsc)
                    ? "Four letters, a zero, then six characters"
                    : "e.g. HDFC0001234"
                }
                error={Boolean(app.bankIfsc) && !isValidIfsc(app.bankIfsc)}
                value={app.bankIfsc}
                onChange={(e) => set("bankIfsc", e.target.value.toUpperCase())}
              />
            </Grid>
            {field("bankName", "Bank name", undefined, 3)}

            {/* 🔑 SEPARATE COMPANIES ARE SEPARATE REGISTRATIONS, and this note
                is here because the arrangement is common rather than exotic:
                providers often keep internet revenue in one GST-registered
                company and everything else in another.

                An invoice carries the registered person who issued it, so two
                GSTINs under two PANs are two legal persons and cannot share one
                registration -- the GSTIN/PAN cross-check in validateApplication
                would reject the pair anyway. Two GSTINs on the SAME PAN are a
                different thing (one entity, several states) and `tax` is a list
                precisely so those fit. ⚠️ Saying this here is guidance only; the
                platform does not yet link the two namespaces to each other. */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="caption" color="text.secondary">
                If you keep internet revenue in a separate GST-registered company from your other
                services, register each company separately — an invoice carries the registered
                person who issued it, so the two cannot share one account. Several GST
                registrations under one PAN, for different states, do belong on this one form.
              </Typography>
            </Grid>

            {/* ⛔ The licence section renders ONLY for licensed kinds. Showing it
                greyed out to a SaaS provider still tells them they are missing
                something they can never supply. */}
            {licensed && (
              <>
            <Grid size={{ xs: 12 }}>
              <Divider textAlign="left"><Typography variant="caption">Licence</Typography></Divider>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Checkbox checked={app.holdsLicense}
                    onChange={(e) => set("holdsLicense", e.target.checked)} />
                }
                label="This provider holds its own telecom or ISP licence"
              />
            </Grid>
            {app.holdsLicense ? (
              <>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <TextField select fullWidth size="small" label="Licence type"
                    value={app.licenseType}
                    onChange={(e) => set("licenseType", e.target.value as OperatorApplication["licenseType"])}>
                    {LICENSE_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                  </TextField>
                </Grid>
                {field("licenseNumber", "Licence number", undefined, 3)}
                {field("licensedEntity", "Entity named on the licence", "May differ from legal name", 3)}
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <TextField fullWidth size="small" type="date" label="Valid from"
                    slotProps={{ inputLabel: { shrink: true } }}
                    value={app.licenseValidFrom}
                    onChange={(e) => set("licenseValidFrom", e.target.value)} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <TextField fullWidth size="small" type="date" label="Valid to"
                    slotProps={{ inputLabel: { shrink: true } }}
                    value={app.licenseValidTo}
                    onChange={(e) => set("licenseValidTo", e.target.value)} />
                </Grid>
              </>
            ) : (
              field("upstreamNamespace", "Upstream provider namespace", "Whose licence your internet service is invoiced under", 4)
            )}
              </>
            )}

            <Grid size={{ xs: 12 }}>
              <Divider textAlign="left"><Typography variant="caption">Address and contact</Typography></Divider>
            </Grid>
            {field("addressLine1", "Address line 1", undefined, 6)}
            {field("addressLine2", "Address line 2", undefined, 6)}
            {field("city", "City", undefined, 3)}
            {field("state", "State", "Determines place of supply", 3)}
            {field("pincode", "Pincode", undefined, 3)}
            {field("contactName", "Contact name", undefined, 3)}
            {field("contactEmail", "Contact email", "Your sign-in will be created against this", 4)}
            {field("contactMobile", "Contact mobile", undefined, 4)}
          </Grid>
        </Paper>

        <Dialog open={guideOpen} onClose={() => setGuideOpen(false)} maxWidth="md" fullWidth scroll="paper">
          <DialogContent dividers>
            <NamespaceGuide />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setGuideOpen(false)}>Close</Button>
            <Button
              variant="contained"
              onClick={() => { set("namespaceGuideRead", true); setGuideOpen(false); }}
            >
              I have read this
            </Button>
          </DialogActions>
        </Dialog>

        {invalid.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <ul style={{ margin: 0, paddingLeft: "1.2em" }}>
              {invalid.map((p) => <li key={p}>{p}</li>)}
            </ul>
          </Alert>
        )}

        {/* 🔑 THREE OUTCOMES, EACH SAYING SOMETHING DIFFERENT. Collapsing
            them into one "something went wrong" would hide the only one the
            applicant can act on -- the server's own validation list. */}
        {acceptedId && (
          <Alert severity="success" sx={{ mb: 2 }}>
            <AlertTitle>Application received</AlertTitle>
            Your reference is <code>{acceptedId}</code>. Keep it — quoting it is how we
            find your application. A person reviews every application; we will contact
            you on the email and mobile above.
          </Alert>
        )}

        {/* 🔑 THE SERVER'S LIST, VERBATIM. This route is public, so the browser
            rules above are bypassed by anyone posting JSON and the server
            re-validates everything. When it disagrees with us, IT is right --
            replacing its reasons with a generic message would leave the
            applicant unable to fix the thing that was actually refused. */}
        {problems && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>Not accepted — please correct these</AlertTitle>
            {problems.length > 0 ? (
              <ul style={{ margin: 0, paddingLeft: "1.2em" }}>
                {problems.map((p) => <li key={p}>{p}</li>)}
              </ul>
            ) : (
              <>The application was refused, but no reason was given.</>
            )}
          </Alert>
        )}

        {/* ⚠️ A transport failure is NOT a validation failure, and the wording
            has to keep them apart: the applicant's details may be perfect. It
            also must not tell them to re-enter anything -- nothing here is
            persisted, so a reload loses the form. */}
        {failure && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <AlertTitle>Could not submit</AlertTitle>
            {failure} Your details are still on this page — please try again in a
            moment, or write to <Link href="mailto:sales@ankhos.io">sales@ankhos.io</Link>.
          </Alert>
        )}

        {/* 🔑 The policies sit NEXT TO the submit control, not in a footer.
            This is the point at which someone is asked to hand over their PAN,
            GSTIN, bank details and directors' identity documents, so the terms
            governing that handover belong where the decision is made rather
            than a scroll away. */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          By submitting you agree to our{" "}
          <Link component={RouterLink} to="/legal/terms" target="_blank" rel="noopener">
            Terms of Service
          </Link>
          , and confirm you have read our{" "}
          <Link component={RouterLink} to="/legal/privacy" target="_blank" rel="noopener">
            Privacy Policy
          </Link>{" "}
          and our{" "}
          <Link component={RouterLink} to="/legal/refunds" target="_blank" rel="noopener">
            Cancellation &amp; Refunds
          </Link>{" "}
          terms. Pricing is on the{" "}
          <Link component={RouterLink} to="/pricing" target="_blank" rel="noopener">
            plans page
          </Link>
          .
          {/* 🪤 The free-period claim used to be restated here as well as in
              TrialHighlight above. Two copies of a commercial promise is two
              chances to state a different one -- the exact defect that once
              shipped "Your first the first 3 months are free" -- and the e2e
              test asserts exactly one occurrence per page. State it once, in
              the component, and link to the rest. */}
        </Typography>

        {/* Owner 2026-10-24: a human check on every way in, the intake too. */}
        <Box sx={{ mb: 2 }}>
          <HumanCheck action="signup" />
        </Box>
        <Button
          variant="contained"
          disabled={invalid.length > 0 || sending || acceptedId !== null}
          onClick={() => void send()}
        >
          {sending ? "Submitting…" : "Submit application"}
        </Button>
      <VersionLine />
      </Container>
    </Box>
  );
}
